/**
 * Compact preference row — memoized, only re-renders when its own selected state changes.
 */
import React, { memo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { androidScaleFont, androidFlatSurfaceStyle } from '../../shared/theme/studio/stackScreenShell';

type Props = {
  colors: ThemeColors;
  title: string;
  breadcrumb?: string;
  selected: boolean;
  onPress: () => void;
};

function createStyles(c: ThemeColors) {
  const sf = androidScaleFont;

  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: 48,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderRadius: appTheme.borderRadius.lg,
      marginBottom: appTheme.spacing.xs,
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${c.text}10`,
      ...androidFlatSurfaceStyle(),
    },
    row_selected: {
      backgroundColor: c.primaryUltra ?? `${c.primary}12`,
      borderColor: c.primary,
    },
    text_col: {
      flex: 1,
      minWidth: 0,
      marginEnd: appTheme.spacing.sm,
    },
    title: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      color: c.text,
      fontWeight: '500',
    },
    title_selected: {
      color: c.primary,
      fontWeight: '700',
    },
    breadcrumb: {
      ...appTheme.typography.caption,
      fontSize: sf(appTheme.typography.caption.fontSize ?? 12),
      color: c.textSecondary,
      marginTop: 2,
    },
    check_wrap: {
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: c.border,
      backgroundColor: c.surface,
    },
    check_wrap_selected: {
      borderColor: c.primary,
      backgroundColor: c.primary,
    },
  });
}

function PreferencesItemRowInner({ colors, title, breadcrumb, selected, onPress }: Props) {
  const styles = createStyles(colors);

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      style={({ pressed }) => [styles.row, selected && styles.row_selected, pressed && { opacity: 0.88 }]}>
      <View style={styles.text_col}>
        <Text style={[styles.title, selected && styles.title_selected]} numberOfLines={2}>
          {title}
        </Text>
        {breadcrumb ? (
          <Text style={styles.breadcrumb} numberOfLines={1}>
            {breadcrumb}
          </Text>
        ) : null}
      </View>
      <View style={[styles.check_wrap, selected && styles.check_wrap_selected]}>
        {selected ? <SvgIcon name="check" size={14} color={colors.onPrimary ?? colors.surface} /> : null}
      </View>
    </Pressable>
  );
}

export const PreferencesItemRow = memo(
  PreferencesItemRowInner,
  (prev, next) =>
    prev.selected === next.selected &&
    prev.title === next.title &&
    prev.breadcrumb === next.breadcrumb &&
    prev.colors === next.colors &&
    prev.onPress === next.onPress,
);
